import React from "react";
import { formatDate } from "../../helpers";
import { DateValue, HostDetails } from "./types";
import { AdvancedFilters } from "../WhatsOnHero/types";

interface WhatsOnStateToggleProps {
  titleSearchTerm: string;
  dateValue: DateValue;
  resetDates: () => void;
  resetHost: () => void;
  resetTitle: () => void;
  advancedFilters: AdvancedFilters;
  setAdvancedFilters: (filters: AdvancedFilters) => void;
  hostOptions: HostDetails[];
  setHostOptions: (hosts: HostDetails[]) => void;
}

const ToggleItem = (props: {
  text: string;
  clickCallback: Function;
  title: string;
}) => {
  const { text, clickCallback, title } = props;
  return (
    <button
      title={title}
      className="whats-on__state-toggle"
      onClick={() => {
        clickCallback(text);
      }}
    >
      {text}
    </button>
  );
};

const WhatsOnStateToggleSection = (props: WhatsOnStateToggleProps) => {
  const {
    titleSearchTerm,
    dateValue,
    resetDates,
    resetTitle,
    advancedFilters,
    setAdvancedFilters,
    hostOptions,
    setHostOptions
  } = props;

  const dateText = ` ${formatDate(dateValue.startDate).shortNoComma || ""}${
    dateValue.startDate && dateValue.endDate ? ` to ` : ""
  }${formatDate(dateValue.endDate).shortNoComma || ""}`;

  // there needs to be a date, and it can't be the default date
  const showDateToggle = dateValue.startDate || dateValue.endDate;

  const allSurfacesSelected: boolean =
    advancedFilters.courtSurfaces.filter(value => {
      return !value.active;
    }).length === 0;

  const oneSurfacesSelected: boolean =
    advancedFilters.courtSurfaces.filter(value => {
      return value.active;
    }).length === 1;

  const allDoornessSelected: boolean =
    advancedFilters.courtDoorness.filter(value => {
      return !value.active;
    }).length === 0;

  const selectedCats = advancedFilters.categories.filter(value => {
    return value.selected;
  });

  const isCategorySpecified: boolean = selectedCats.length !== 0;

  const selectedDrawFormat = advancedFilters.drawFormat
    ? advancedFilters.drawFormat.filter(value => {
        return value.selected;
      })
    : [];

  const isDrawFormatSpecified: boolean = selectedDrawFormat.length !== 0;

  return (
    <div className="whatson-state-section page-section">
      <div className="page-section__inner">
        {titleSearchTerm || dateValue.startDate || dateValue.endDate ? (
          <span>
            {showDateToggle ? (
              <ToggleItem
                text={dateText}
                clickCallback={resetDates}
                title="Remove Date Filter"
              />
            ) : null}
            {/* {hostSearchText ? (
              <ToggleItem
                text={hostSearchText}
                clickCallback={resetHost}
                title="Remove Host Filter"
              />
            ) : null} */}
            {titleSearchTerm ? (
              <ToggleItem
                text={`Title: ${titleSearchTerm}`}
                clickCallback={resetTitle}
                title="Remove Title Search"
              />
            ) : null}
            {!allSurfacesSelected && !oneSurfacesSelected
              ? advancedFilters.courtSurfaces.map((surface, i) => {
                  if (surface.active) {
                    return (
                      <ToggleItem
                        text={`${surface.name}`}
                        key={`surface-${i}`}
                        // remove this surface
                        clickCallback={() => {
                          let nextSurfaces = advancedFilters.courtSurfaces.map(
                            nextSurface => {
                              if (surface.code === nextSurface.code) {
                                return {
                                  ...nextSurface,
                                  active: false
                                };
                              } else {
                                return nextSurface;
                              }
                            }
                          );
                          setAdvancedFilters({
                            ...advancedFilters,
                            courtSurfaces: nextSurfaces
                          });
                        }}
                        title={`Remove ${surface.name}`}
                      />
                    );
                  } else {
                    return null;
                  }
                })
              : null}
            {!allSurfacesSelected && oneSurfacesSelected
              ? advancedFilters.courtSurfaces.map((surface, i) => {
                  if (surface.active) {
                    return (
                      <ToggleItem
                        text={`${surface.name}`}
                        key={`surface-${i}`}
                        // reset to all selected
                        clickCallback={() => {
                          let nextSurfaces = advancedFilters.courtSurfaces.map(
                            surface => {
                              return {
                                ...surface,
                                active: true
                              };
                            }
                          );
                          setAdvancedFilters({
                            ...advancedFilters,
                            courtSurfaces: nextSurfaces
                          });
                        }}
                        title={`Remove ${surface.name}`}
                      />
                    );
                  } else {
                    return null;
                  }
                })
              : null}
            {!allDoornessSelected
              ? advancedFilters.courtDoorness.map((doorness, i) => {
                  if (doorness.active) {
                    return (
                      <ToggleItem
                        text={`${doorness.code === "O" ? "Outdoor" : "Indoor"}`}
                        key={`doorness-${i}`}
                        // reset to all selected
                        clickCallback={() => {
                          let nextDoorness = advancedFilters.courtDoorness.map(
                            door => {
                              return {
                                ...door,
                                active: true
                              };
                            }
                          );
                          setAdvancedFilters({
                            ...advancedFilters,
                            courtDoorness: nextDoorness
                          });
                        }}
                        title={`Remove ${doorness.code}`}
                      />
                    );
                  } else {
                    return null;
                  }
                })
              : null}
            {isDrawFormatSpecified
              ? selectedDrawFormat.map(item => (
                  <ToggleItem
                    key={item.value}
                    text={`${item && item.label}`}
                    clickCallback={(label: string) => {
                      let nextDrawFormat =
                        advancedFilters.drawFormat &&
                        advancedFilters.drawFormat.map(item => {
                          return {
                            ...item,
                            selected: false
                          };
                        });

                      let AllOption =
                        advancedFilters.drawFormat &&
                        advancedFilters.drawFormat.map(item => ({
                          ...item,
                          selected: item.value === "All"
                        }));

                      if (label === "All Draw Formats") {
                        return;
                      }

                      setAdvancedFilters({
                        ...advancedFilters,
                        drawFormat:
                          selectedDrawFormat.length > 0
                            ? nextDrawFormat
                            : AllOption
                      });
                    }}
                    title={`Remove ${item.label}`}
                  />
                ))
              : null}
            {isCategorySpecified
              ? selectedCats.map(selectedCat => (
                  <ToggleItem
                    key={selectedCat.value}
                    text={`${selectedCat && selectedCat.label}`}
                    // reset to all selected
                    clickCallback={(label: string) => {
                      let nextCategories = advancedFilters.categories.map(
                        cat => {
                          if (cat.label == label) {
                            return {
                              ...cat,
                              selected: !cat.selected
                            };
                          }
                          return {
                            ...cat,
                            selected: cat.selected
                          };
                        }
                      );

                      let AllOption = advancedFilters.categories.map(cat => ({
                        ...cat,
                        selected: cat.value === "All"
                      }));

                      if (label === "All Categories") {
                        return;
                      }

                      setAdvancedFilters({
                        ...advancedFilters,
                        categories:
                          selectedCats.length > 1 ? nextCategories : AllOption
                      });
                    }}
                    title={`Remove ${selectedCat && selectedCat.value}`}
                  />
                ))
              : null}
            {hostOptions.map((host, i) => {
              let output = null;
              if (host.active) {
                output = (
                  <ToggleItem
                    text={`${host.name}`}
                    key={`host-${i}`}
                    // reset to all selected
                    clickCallback={() => {
                      let nextHostOptions: HostDetails[] = hostOptions.map(
                        thisHost => {
                          if (thisHost.code === host.code) {
                            return {
                              ...thisHost,
                              active: false
                            };
                          } else {
                            return {
                              ...thisHost
                            };
                          }
                        }
                      );
                      setHostOptions(nextHostOptions);
                    }}
                    title={`Remove ${host.name}`}
                  />
                );
              }
              return output;
            })}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default WhatsOnStateToggleSection;
